import { ValidationErrors } from '@angular/forms';
import { portalError } from '@app/utils/error/portal-error';
import { ValidationErrorMap } from './validation-error-map';

interface BaseOptions {
  i18nKey: string;
  i18nParameters?: any;
  key: string;
}

interface InvalidOptions extends BaseOptions {
  /**
   * Whether the value is invalid. The validation message is added if it is false.
   * Invalid is mutually exclusive with valid.
   */
  invalid?: boolean;
}

interface ValidOptions extends BaseOptions {
  /**
   * Whether the value is valid. The validation message is added if it is true.
   * Valid is mutually exclusive with invalid.
   */
  valid?: boolean;
}

export class ValidationErrorHandler {
  private errors?: ValidationErrorMap;

  /**
   * If `test` is true: Returns a validation error that can be passed to a reactive form validator
   * If `test` is false: Returns null
   * @param i18nKey
   * @param i18nParameters
   * @param key
   * @param test
   */
  static validate({
    i18nKey,
    i18nParameters,
    key,
    ...other
  }: InvalidOptions | ValidOptions): ValidationErrors | null {
    let predicate: boolean;

    if ('invalid' in other && 'valid' in other) {
      throw portalError({
        cause: new Error('Invalid and valid cannot be used together.'),
        id: 'validation-error-handler.validate:invalid-arguments',
      });
    }

    if ('invalid' in other) {
      predicate = other.invalid === true;
    }

    if ('valid' in other) {
      predicate = other.valid === false;
    }

    return new ValidationErrorHandler()
      .addError(key, { key: i18nKey, parameters: i18nParameters }, predicate)
      .getErrors();
  }

  public addAll(errors: ValidationErrors | null, ignore?: string[]): this {
    if (errors != null) {
      Object.entries(errors).forEach(([key, value]) => {
        if (ignore?.includes(key) !== true) {
          this.addError(key, value);
        }
      });
    }

    return this;
  }

  /**
   * Adds a validation error for the whole form.
   *
   * @param key a property name that is not defined in `DefaultKeys` (validation-error-map.ts)
   * @param value a `{key:string, parameters?: any}` for translation
   * @param predicate if true the validation error will be added
   */
  public addError(key: string, value: { key: string; parameters?: any }, predicate: boolean = true): this {
    if (predicate === true) {
      if (this.errors === undefined) {
        this.errors = {};
      }
      this.errors[key] = value;
    }

    return this;
  }

  public getErrors(): ValidationErrors | null {
    return this.errors;
  }

  public hasErrors(): boolean {
    return this.errors !== undefined;
  }
}
